import { useTracking } from '@components/hooks';
import { usePageData } from '@components/context';
import { useCookieConsent } from '@wienenergiegithub/modules-next/components';
import React, { useEffect, useMemo, useRef } from 'react';

const ScrollObserver = ({
  eventName,
  trackEvent,
  children,
  ScrollIndexVertical,
  trackingName,
  bump = true,
}) => {
  const track = useTracking();
  const cookieConsent = useCookieConsent();
  const hasCookieConsent = useMemo(
    () => cookieConsent.services.length > 0,
    [cookieConsent.services.length],
  );
  const { type } = usePageData();

  const getScrollIndex = i => {
    // our best scroll index guess - 1
    let si = 1;

    // if a scrollindex is given to the function, we use it (+1)
    if (i !== undefined) {
      si = i + 1;
      // when cms Components are used on product pages, we bump them up (because product stuff is before)
      // we give the oberservers bump = false so that only cms components are bumped
      if (type && type.includes('product') && bump) {
        si += 2;
      }
    } else {
      // no vertical scroll index is given, we take the next best from content-tracking-view
      const ecEvents = window.dataLayer.filter(
        e => e.event === 'content-tracking-view',
      );
      if (ecEvents.length) {
        si = ecEvents.reduce((acc, e) => {
          const positions =
            e.ecommerce.promoView.promotions[0].position.split('.');
          // if position is higher than acc, we return the position
          if (positions[0] > acc) {
            return parseInt(positions[0], 10);
          }
          return acc;
        }, 0);
      }
    }

    return si;
  };

  const scrollIndex = getScrollIndex(ScrollIndexVertical);

  const pushDataLayer = () => {
    if (trackEvent && hasCookieConsent) {
      track({
        ...trackEvent,
        event: eventName,
      });
    } else if (trackingName) {
      track({
        event: 'content-tracking-view',
        eventCategory: 'Content View',
        eventAction: `${trackingName}`,
        eventName: `Pos: ${scrollIndex}`,
      });
    }
  };
  // We check that google dataLayer is ready
  const googleIsReady = window.dataLayer.length > 2;
  useEffect(() => {
    if (googleIsReady && scrollIndex === 1) {
      pushDataLayer();
    }
  }, [googleIsReady]);

  const ref = useRef();
  const observer = new IntersectionObserver(
    (entries, intersectionObserver) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          intersectionObserver.unobserve(ref.current);
        }

        if (entry.intersectionRatio <= 0 || scrollIndex === 1) {
          return;
        }

        pushDataLayer();
      });
    },
    {
      threshold: 0.5,
    },
  );

  useEffect(() => {
    if (!ref.current) {
      return () => {};
    }

    observer.observe(ref.current);

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref.current]);

  return <div ref={ref}>{children}</div>;
};

export default ScrollObserver;
